import type { ILead } from 'src/services/api/leads/types';
import type { IStageHistory, ILeadPropertyInterest } from 'src/services/api/lead-stage-history/types';
import { ELeadStage } from 'src/services/api/leads/types';
import { normalizeSource } from 'src/pages/landlord/analytics/utils/normalize-source';
import { normalizeIngestionMethod } from 'src/components/lead-sidebar/normalize-ingestion-method';
import { formatTimelineDate } from 'src/utils/format-date-time';

const MAX_INGESTION_SOURCE_LENGTH = 20;

const getIngestionSourceWithEllipsis = (source: string): string => {
    if (source.length <= MAX_INGESTION_SOURCE_LENGTH) {
        return source;
    }
    return `${source.substring(0, MAX_INGESTION_SOURCE_LENGTH)}...`;
};

export interface IStageCopy {
    label: string;
    details: string;
}

/**
 * Copy for every lead stage. Typed as an exhaustive `Record<ELeadStage, ...>` so that adding a
 * value to `ELeadStage` fails type-check until the timeline knows how to describe it — previously
 * this was a plain array and `tour_completed` was silently missing from it.
 */
export const getStageCopy = (lead: ILead, leadOwnerName: string): Record<ELeadStage, IStageCopy> => ({
    [ELeadStage.NEW]: {
        label: 'New lead created',
        details: `From ${getIngestionSourceWithEllipsis(normalizeSource(lead.ingestion_source))} via ${normalizeIngestionMethod(lead.ingestion_method)}`,
    },
    [ELeadStage.CONTACTED]: {
        label: 'Lead contacted',
        details: `${leadOwnerName} contacted the lead`,
    },
    [ELeadStage.RESPONSIVE]: {
        label: 'Lead responded',
        details: `${lead.name} replied to ${leadOwnerName}`,
    },
    [ELeadStage.TOUR_REQUESTED]: {
        label: 'Tour requested',
        details: `Tour requested by ${lead.name}`,
    },
    [ELeadStage.TOUR_SCHEDULED]: {
        label: 'Tour scheduled',
        details: `Tour scheduled for ${lead.name}`,
    },
    [ELeadStage.TOUR_COMPLETED]: {
        label: 'Tour completed',
        details: `${lead.name} completed a tour`,
    },
    [ELeadStage.INVITED_TO_APPLY]: {
        label: 'Application sent',
        details: `Application sent to ${lead.name}`,
    },
    [ELeadStage.APPLICATION_COMPLETED]: {
        label: 'Application completed',
        details: `Application completed by ${lead.name}`,
    },
    [ELeadStage.APPLICATION_APPROVED]: {
        label: 'Application approved',
        details: `Application approved for ${lead.name}`,
    },
    [ELeadStage.LEASE_SIGNED]: {
        label: 'Lease Signed',
        details: `${lead.name} has signed the lease`,
    },
    [ELeadStage.NOT_INTERESTED]: {
        label: 'Not Interested',
        details: `${lead.name} no longer interested`,
    },
    [ELeadStage.NOT_QUALIFIED]: {
        label: 'Not Qualified',
        details: `${lead.name} is not qualified`,
    },
});

const humanizeStageValue = (stageValue: string): string => stageValue
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Fallback copy for a stage value the backend sends that this build does not know about. Showing a
 * humanized label keeps the event on the timeline instead of dropping it without a trace.
 */
export const getUnknownStageCopy = (stageValue: string): IStageCopy => ({
    label: humanizeStageValue(stageValue),
    details: `Lead stage changed to ${humanizeStageValue(stageValue)}`,
});

export type TStageOrigin = 'auto' | 'manual';

/**
 * Human-readable explanation for each `reason` the backend records against a stage change. Keyed by
 * the raw value; anything unrecognised falls back to the generic copy for its `auto`/`manual`
 * prefix, which the backend guarantees.
 */
const STAGE_REASON_DETAILS: Record<string, string> = {
    manual: 'Set manually by a team member',
    manual_select_nudge: 'Set manually from a suggested nudge',
    manual_recommend_nudge: 'Set manually from a recommended nudge',
    auto: 'Set automatically by Reffie',
    auto_email_system_message_type: 'Set automatically from an email update',
    auto_after_showing_inserted: 'Set automatically after a tour was booked',
    auto_after_tour_cancelled: 'Set automatically after a tour was cancelled',
    auto_after_tour_rescheduled: 'Set automatically after a tour was rescheduled',
    auto_after_message_inserted: 'Set automatically after a new message',
    auto_after_lead_inserted: 'Set automatically when the lead was created',
};

/**
 * The backend guarantees every `LeadStageChangeReason` starts with `manual` or `auto`, so classify
 * on the prefix rather than an exact match — reasons are added over time and an unknown one should
 * still show the right provenance instead of nothing.
 */
export const getStageOrigin = (reason?: string | null): TStageOrigin | null => {
    if (!reason) {
        return null;
    }
    if (reason.startsWith('manual')) {
        return 'manual';
    }
    if (reason.startsWith('auto')) {
        return 'auto';
    }
    return null;
};

export const getStageOriginLabel = (origin: TStageOrigin): string => (
    origin === 'manual' ? 'Manual' : 'Automatic'
);

export const getStageOriginDetails = (reason: string, origin: TStageOrigin): string => (
    STAGE_REASON_DETAILS[reason] ?? STAGE_REASON_DETAILS[origin]
);

/**
 * Ingestion methods that only happen because a person did something. Everything else in
 * `LeadIngestionMethod` (webhooks, inbound SMS/email, Facebook, scheduled report imports) captures
 * the lead without anyone acting, so it is treated as automatic.
 */
const MANUAL_INGESTION_METHODS = new Set(['manual', 'csv-file', 'onboarding-import']);

export const getLeadCreationOrigin = (ingestionMethod: string): TStageOrigin => (
    MANUAL_INGESTION_METHODS.has(ingestionMethod) ? 'manual' : 'auto'
);

export const getLeadCreationOriginDetails = (ingestionMethod: string, origin: TStageOrigin): string => (
    origin === 'manual' ?
        'Lead was added manually by a team member' :
        `Lead was captured automatically via ${normalizeIngestionMethod(ingestionMethod)}`
);

/**
 * Actor prefixes the backend uses when a person — rather than a matching service — created the
 * property-interest record.
 */
const MANUAL_INTEREST_ACTOR_PREFIXES = ['ll_user:', 'manual:'];

const INTEREST_ACTOR_DETAILS: Record<string, string> = {
    'facebook-property-matcher': 'Matched automatically from Facebook',
    'yardi-import': 'Matched automatically from a Yardi import',
    'api-server-backfiller': 'Matched automatically by Reffie',
};

export const getInterestOrigin = (actor?: string | null): TStageOrigin | null => {
    if (!actor) {
        return null;
    }
    return MANUAL_INTEREST_ACTOR_PREFIXES.some(prefix => actor.startsWith(prefix)) ? 'manual' : 'auto';
};

export const getInterestOriginDetails = (actor: string, origin: TStageOrigin): string => {
    if (origin === 'manual') {
        return actor.startsWith('manual:admin:') ?
            'Matched manually by a Reffie admin' :
            'Matched manually by a team member';
    }
    return INTEREST_ACTOR_DETAILS[actor] ?? 'Matched automatically by Reffie';
};

export type TTimelineEvent =
    | {
        kind: 'stage';
        key: string;
        stageValue: string;
        label: string;
        details: string;
        timestamp: string;
        timeValue: string;
        origin: TStageOrigin | null;
        originLabel: string | null;
        originDetails: string | null;
    }
    | {
        kind: 'interest';
        key: string;
        interest: ILeadPropertyInterest;
        timestamp: string;
        timeValue: string;
        origin: TStageOrigin | null;
        originLabel: string | null;
        originDetails: string | null;
    };

/**
 * Builds the chronologically sorted timeline shown in the lead sidebar: the synthetic "new lead"
 * event, every stage transition from the history endpoint, and every property interest.
 */
export const buildTimelineEvents = ({
    lead,
    stages,
    interests,
    leadOwnerName,
    timezone,
}: {
    lead: ILead;
    stages: IStageHistory[];
    interests: ILeadPropertyInterest[];
    leadOwnerName: string;
    timezone?: string | null;
}): TTimelineEvent[] => {
    const stageCopy = getStageCopy(lead, leadOwnerName);
    const creationOrigin = getLeadCreationOrigin(lead.ingestion_method);

    const newLeadEvent: TTimelineEvent = {
        kind: 'stage',
        key: `stage-new-${lead.inserted_at}`,
        stageValue: ELeadStage.NEW,
        ...stageCopy[ELeadStage.NEW],
        timestamp: lead.inserted_at,
        timeValue: formatTimelineDate(lead.inserted_at, timezone),
        origin: creationOrigin,
        originLabel: getStageOriginLabel(creationOrigin),
        originDetails: getLeadCreationOriginDetails(lead.ingestion_method, creationOrigin),
    };

    const stageEvents: TTimelineEvent[] = [...stages]
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        .map(history => {
            const origin = getStageOrigin(history.reason);

            return {
                kind: 'stage' as const,
                key: `stage-${history.id}`,
                stageValue: history.current_value,
                ...(stageCopy[history.current_value as ELeadStage] ?? getUnknownStageCopy(history.current_value)),
                timestamp: history.created_at,
                timeValue: formatTimelineDate(history.created_at, timezone),
                origin,
                originLabel: origin && getStageOriginLabel(origin),
                originDetails: origin && getStageOriginDetails(history.reason, origin),
            };
        });

    const interestEvents: TTimelineEvent[] = interests.map(interest => {
        const origin = getInterestOrigin(interest.actor);

        return {
            kind: 'interest' as const,
            key: `interest-${interest.id}`,
            interest,
            timestamp: interest.created_at,
            timeValue: formatTimelineDate(interest.created_at, timezone),
            origin,
            originLabel: origin && getStageOriginLabel(origin),
            originDetails: origin && getInterestOriginDetails(interest.actor!, origin),
        };
    });

    return [newLeadEvent, ...stageEvents, ...interestEvents].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
};
